import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  GridItem,
  useAttributeScores,
  useMarkAbsent,
  usePutScoreCell,
  useScoreGrid,
} from "@/api/school";

/** 录分 — Exam Engine P1 FR-D. Item-level grid: objective items default
 *  to correct and the teacher only clicks the wrong ones; subjective
 *  items take numeric scores. 完成录分 runs KP attribution + profile
 *  writeback server-side. */
export default function ScoreGridPage() {
  const { t } = useTranslation();
  const { examId } = useParams<{ examId: string }>();
  const grid = useScoreGrid(examId);
  const putCell = usePutScoreCell(examId!);
  const markAbsent = useMarkAbsent(examId!);
  const attribute = useAttributeScores(examId!);

  // Optimistic local layer over the server bundle; key `${student}:${item}`.
  const [local, setLocal] = useState<Record<string, { score: number | null; is_correct: boolean | null }>>({});
  const [missing, setMissing] = useState<{ student_id: string; name: string; missing_seqs: number[] }[] | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const g = grid.data;
  if (grid.isLoading) return <p className="p-6 text-sm text-slate-400">…</p>;
  if (grid.isError)
    return (
      <p className="p-6 text-sm text-rose-500">
        {(grid.error as { detail?: string })?.detail ?? t("scoreGrid.notReady")}
      </p>
    );
  if (!g) return null;

  const absentSet = new Set(g.absent);

  function cellOf(sid: string, itemId: string) {
    return local[`${sid}:${itemId}`] ?? g!.cells[sid]?.[itemId] ?? null;
  }

  function save(sid: string, item: GridItem, patch: { score?: number | null; is_correct?: boolean | null }) {
    const next = {
      score: patch.score !== undefined ? patch.score : null,
      is_correct: patch.is_correct !== undefined ? patch.is_correct : null,
    };
    if (next.is_correct !== null) next.score = next.is_correct ? item.max : 0;
    setLocal((m) => ({ ...m, [`${sid}:${item.item_id}`]: next }));
    putCell.mutate(
      { student_id: sid, item_id: item.item_id, score: patch.score, is_correct: patch.is_correct },
      {
        onError: () => {
          // Roll the optimistic cell back to the server value.
          setLocal((m) => {
            const { [`${sid}:${item.item_id}`]: _drop, ...rest } = m;
            return rest;
          });
        },
      },
    );
  }

  function toggleObjective(sid: string, item: GridItem) {
    const cur = cellOf(sid, item.item_id);
    // No row = default correct (D1). Click → wrong; click again → correct.
    const wasCorrect = cur ? cur.is_correct !== false : true;
    save(sid, item, { is_correct: !wasCorrect });
  }

  function studentTotal(sid: string): number {
    let sum = 0;
    for (const it of g!.items) {
      const c = cellOf(sid, it.item_id);
      if (c?.score != null) sum += c.score;
      else if (it.objective) sum += it.max; // display default-correct
    }
    return sum;
  }

  function runAttribute(allowPartial: boolean) {
    setMissing(null);
    setDone(null);
    attribute.mutate(
      { allow_partial: allowPartial },
      {
        onSuccess: (r) => {
          setLocal({});
          setDone(
            t("scoreGrid.doneSummary", {
              attributed: r.attributed,
              absent: r.absent,
              kps: r.kp_mastery_upserts,
              skipped: r.skipped_incomplete.length ? r.skipped_incomplete.join("、") : "-",
            }),
          );
        },
        onError: (err) => {
          const d = (err as { detail?: { missing?: typeof missing } }).detail;
          if (d && typeof d === "object" && d.missing) setMissing(d.missing);
        },
      },
    );
  }

  return (
    <div className="p-6 mx-auto max-w-full">
      <header className="mb-4 flex items-end justify-between gap-4">
        <div>
          <Link to={`/exams/${examId}/paper`} className="text-xs text-sky-600 hover:underline">
            ← {t("scoreGrid.backToPaper")}
          </Link>
          <h1 className="text-2xl font-semibold text-slate-800 mt-1">
            {t("scoreGrid.title")} · {g.exam.title}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {g.exam.class_name} · {t("scoreGrid.fullMarks")} {g.exam.max_total} ·{" "}
            {t("scoreGrid.paperVersion", { version: g.paper.version })} ·{" "}
            {t("scoreGrid.objectiveHint")}
          </p>
        </div>
        <button
          onClick={() => runAttribute(false)}
          disabled={attribute.isPending}
          className="shrink-0 rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          {attribute.isPending ? t("scoreGrid.attributing") : t("scoreGrid.finishAndAttribute")}
        </button>
      </header>

      {done && (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
          {done}
        </div>
      )}
      {missing && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium mb-1">{t("scoreGrid.missingTitle")}</p>
          <ul className="list-disc pl-5 mb-2">
            {missing.map((m) => (
              <li key={m.student_id}>
                {m.name}:{t("scoreGrid.missingItems", { seqs: m.missing_seqs.map((s) => `#${s}`).join(" ") })}
              </li>
            ))}
          </ul>
          <button
            onClick={() => runAttribute(true)}
            className="rounded border border-amber-400 bg-white px-3 py-1 text-xs hover:bg-amber-100"
          >
            {t("scoreGrid.attributePartial")}
          </button>
        </div>
      )}

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white" style={{ maxHeight: "70vh" }}>
        <table className="text-xs whitespace-nowrap border-collapse">
          <thead className="sticky top-0 z-20 bg-slate-50">
            <tr>
              <th className="sticky left-0 z-30 bg-slate-50 border-b border-r border-slate-200 px-3 py-2 text-left">
                {t("scoreGrid.student")}
              </th>
              <th className="border-b border-slate-200 px-2 py-2">{t("scoreGrid.absent")}</th>
              {g.items.map((it) => (
                <th key={it.item_id} className="border-b border-slate-200 px-2 py-1 text-center" title={it.section_label ?? ""}>
                  <div className="font-semibold">#{it.seq}</div>
                  <div className="font-normal text-slate-400">
                    {it.max}
                    {t("scoreGrid.pts")}
                    {it.objective ? "" : "✎"}
                  </div>
                </th>
              ))}
              <th className="sticky right-0 z-30 bg-slate-50 border-b border-l border-slate-200 px-3 py-2">
                {t("scoreGrid.total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {g.students.map((s) => {
              const isAbsent = absentSet.has(s.id);
              const isAttributed = g.attributed.includes(s.id);
              return (
                <tr key={s.id} className={isAbsent ? "bg-slate-50 text-slate-300" : "hover:bg-sky-50/40"}>
                  <td className="sticky left-0 z-10 bg-white border-b border-r border-slate-100 px-3 py-1.5">
                    <span className="font-medium text-slate-700">{s.name}</span>
                    {s.student_code && <span className="ml-1.5 text-slate-400">{s.student_code}</span>}
                    {isAttributed && <span className="ml-1.5 text-emerald-500" title={t("scoreGrid.attributed")}>✓</span>}
                  </td>
                  <td className="border-b border-slate-100 px-2 py-1.5 text-center">
                    <input
                      type="checkbox"
                      checked={isAbsent}
                      onChange={(e) => markAbsent.mutate({ student_id: s.id, is_absent: e.target.checked })}
                    />
                  </td>
                  {g.items.map((it) => {
                    if (isAbsent)
                      return (
                        <td key={it.item_id} className="border-b border-slate-100 px-2 py-1.5 text-center">
                          —
                        </td>
                      );
                    const c = cellOf(s.id, it.item_id);
                    if (it.objective) {
                      const correct = c ? c.is_correct !== false : true;
                      return (
                        <td key={it.item_id} className="border-b border-slate-100 p-0 text-center">
                          <button
                            onClick={() => toggleObjective(s.id, it)}
                            className={`h-7 w-full min-w-[2.5rem] font-semibold ${
                              correct ? "text-emerald-600 hover:bg-emerald-50" : "bg-rose-50 text-rose-600 hover:bg-rose-100"
                            }`}
                          >
                            {correct ? "✓" : "✗"}
                          </button>
                        </td>
                      );
                    }
                    return (
                      <td key={it.item_id} className="border-b border-slate-100 p-0 text-center">
                        <input
                          type="number"
                          min={0}
                          max={it.max}
                          step={0.5}
                          defaultValue={c?.score ?? ""}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            const num = v === "" ? null : Math.max(0, Math.min(it.max, Number(v)));
                            if (v !== "" && Number.isNaN(Number(v))) return;
                            if ((c?.score ?? null) === num) return;
                            save(s.id, it, { score: num });
                          }}
                          className="h-7 w-14 border-0 bg-transparent text-center outline-none focus:bg-sky-50"
                        />
                      </td>
                    );
                  })}
                  <td className="sticky right-0 z-10 bg-white border-b border-l border-slate-100 px-3 py-1.5 text-center font-semibold text-slate-700">
                    {isAbsent ? "—" : `${studentTotal(s.id)}`.replace(/\.0$/, "")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
